import { apolloClient } from '~/apollo-client';
import { CustomerAccountType, PerchProductProofDocRequirement } from '~/typings/graphql';
import { FETCH_FUNNEL_CONFIGURATION } from './fetch-funnel-configuration.queries';
import type { CountyProjectMatch, SolarProjectDetails } from './perch-project-details';

export interface ProductSignupContractTemplateEntry {
  perchSfProductId: string;
  signupContractTemplateId: number;
}

export type { SolarProjectDetails } from './perch-project-details';
export { resolveRequireFicoCheck } from './require-fico-check';

type FetchFunnelConfigurationQueryVariables = {
  zipCode: number;
  utilityId: number;
  requireFico: boolean;
  partnerId?: string;
  customerType?: string;
  perchSfProductId?: string;
  stateId?: number;
  programId?: string;
  countyId?: number;
};

type ResolveFunnelConfigResult = {
  signUpContractTemplateId: number;
  paymentProvider: string;
  perchSfProductId: string;
  proofDocRequirement: PerchProductProofDocRequirement;
  savingsPercentage: number;
  isCapacityAvailable?: boolean | null;
  supportedCustomerTypes: string[];
  projectDetails?: SolarProjectDetails;
  productSignupContractTemplateIds?: ProductSignupContractTemplateEntry[];
  requireFicoCheck?: boolean | null;
  requireFicoCheckForLmiProduct?: boolean | null;
  requireFicoCheckForDefaultProduct?: boolean | null;
  countyProjectMatch?: CountyProjectMatch | null;
};

type FetchFunnelConfigurationQueryData = {
  resolveFunnelConfig: ResolveFunnelConfigResult | null;
};

const mapCustomerTypeToApi = (
  customerType: FetchFunnelConfigurationParams['customerType'],
): string | undefined => {
  if (customerType == null) {
    return undefined;
  }
  return customerType;
};

export const fetchFunnelConfiguration = async (
  params: FetchFunnelConfigurationParams,
): Promise<FetchFunnelConfigurationResponse> => {
  const apiCustomerType = mapCustomerTypeToApi(params.customerType);
  const variables: FetchFunnelConfigurationQueryVariables = {
    zipCode: parseInt(params.zipCode, 10),
    utilityId: params.utilityId,
    requireFico: params.requireFico,
  };
  if (params.partnerId != null && params.partnerId !== '') {
    variables.partnerId = params.partnerId;
  }
  if (apiCustomerType != null && apiCustomerType !== '') {
    variables.customerType = apiCustomerType;
  }
  if (params.perchSfProductId != null && params.perchSfProductId !== '') {
    variables.perchSfProductId = params.perchSfProductId;
  }
  if (params.stateId != null) {
    variables.stateId = params.stateId;
  }

  if (params.programId != null && params.programId !== '') {
    variables.programId = params.programId;
  }
  if (params.countyId != null) {
    variables.countyId = params.countyId;
  }

  const { data } = await apolloClient.query<
    FetchFunnelConfigurationQueryData,
    FetchFunnelConfigurationQueryVariables
  >({
    query: FETCH_FUNNEL_CONFIGURATION,
    variables,
    fetchPolicy: 'no-cache',
  });

  const resolved = data?.resolveFunnelConfig;
  if (!resolved) {
    throw new Error('Funnel configuration could not be resolved');
  }

  const base: FetchFunnelConfigurationResponse = {
    signUpContractTemplateId: resolved.signUpContractTemplateId,
    paymentProvider: resolved.paymentProvider,
    perchSfProductId: resolved.perchSfProductId,
    proofDocRequirement: resolved.proofDocRequirement,
    savingsPercentage: resolved.savingsPercentage,
    supportedCustomerTypes: resolved.supportedCustomerTypes,
    isCapacityAvailable: resolved.isCapacityAvailable ?? null,
    requireFicoCheck: resolved.requireFicoCheck ?? false,
    requireFicoCheckForLmiProduct: resolved.requireFicoCheckForLmiProduct ?? false,
    requireFicoCheckForDefaultProduct: resolved.requireFicoCheckForDefaultProduct ?? false,
    countyProjectMatch: resolved.countyProjectMatch ?? undefined,
    ...(resolved.productSignupContractTemplateIds != null && {
      productSignupContractTemplateIds: resolved.productSignupContractTemplateIds,
    }),
  };

  if (resolved.projectDetails != null) {
    return { ...base, projectDetails: resolved.projectDetails };
  }

  return base;
};

export type FetchFunnelConfigurationParams = {
  zipCode: string;
  utilityId: number;
  partnerId?: string;
  customerType?: CustomerAccountType;
  requireFico: boolean;
  perchSfProductId?: string;
  stateId?: number;
  programId?: string;
  countyId?: number;
};

export type FetchFunnelConfigurationResponse = {
  paymentProvider: string;
  perchSfProductId: string;
  productSignupContractTemplateIds?: ProductSignupContractTemplateEntry[];
  proofDocRequirement: PerchProductProofDocRequirement;
  projectDetails?: SolarProjectDetails;
  savingsPercentage: number;
  signUpContractTemplateId: number;
  supportedCustomerTypes: string[];
  isCapacityAvailable?: boolean | null;
  requireFicoCheck?: boolean | null;
  requireFicoCheckForLmiProduct?: boolean | null;
  requireFicoCheckForDefaultProduct?: boolean | null;
  countyProjectMatch?: CountyProjectMatch;
};
